import React from 'react';
import styles from './AddMealPanel.module.css';
import { addDaysYyyyMmDd, getLocalYyyyMmDd } from '../../utils/dateUtils';
import SlideUpPanel from '../common/SlideUpPanel';
import { useTranslation } from '../../i18n/provider';

type Props = {
  open: boolean;
  selectedDate: string;
  onChangeDate: (v: string) => void;
  mealText: string;
  onChangeMealText: (v: string) => void;
  isAnalyzing: boolean;
  error: string | null;
  feedback: string | null;
  analyzedCalories: number;
  analyzedProtein: number;
  analyzedCarbs: number;
  analyzedFats: number;
  onChangeCalories: (v: number) => void;
  onChangeProtein: (v: number) => void;
  onChangeCarbs: (v: number) => void;
  onChangeFats: (v: number) => void;
  onClose: () => void;
  onAnalyze: () => void;
  onSave: () => void;
  disableActions?: boolean;
};

const AddMealPanel: React.FC<Props> = ({
  open,
  selectedDate,
  onChangeDate,
  mealText,
  onChangeMealText,
  isAnalyzing,
  error,
  feedback,
  analyzedCalories,
  analyzedProtein,
  analyzedCarbs,
  analyzedFats,
  onChangeCalories,
  onChangeProtein,
  onChangeCarbs,
  onChangeFats,
  onClose,
  onAnalyze,
  onSave,
  disableActions = false,
}) => {
  const { t } = useTranslation();
  const today = getLocalYyyyMmDd();
  const oneWeekAgo = addDaysYyyyMmDd(today, -7);

  return (
    <SlideUpPanel
      open={open}
      title={t('food.addMeal.title')}
      onClose={onClose}
      footer={
        <div className={styles.footer}>
          <button
            type="button"
            onClick={onSave}
            className={styles.saveButton}
            disabled={disableActions}
            style={disableActions ? { cursor: 'not-allowed', opacity: 0.7 } : undefined}
          >
            {t('food.addMeal.save')}
          </button>
        </div>
      }
    >
      <label className={styles.label}>
        {t('food.addMeal.date')}
        <input
          type="date"
          value={selectedDate}
          min={oneWeekAgo}
          max={today}
          onChange={(e) => onChangeDate(e.target.value)}
          className={styles.input}
        />
      </label>

      <label className={styles.label}>
        {t('food.addMeal.describe')}
        <textarea
          value={mealText}
          onChange={(e) => onChangeMealText(e.target.value)}
          rows={3}
          className={styles.textarea}
          placeholder={t('food.addMeal.placeholder')}
        />
      </label>

      {error && <div className={`${styles.message} ${styles.error}`}>{error}</div>}
      {feedback && <div className={`${styles.message} ${styles.feedback}`}>{feedback}</div>}

      <div className={styles.actions}>
        <button
          type="button"
          disabled={disableActions || isAnalyzing || mealText.trim().length === 0}
          onClick={onAnalyze}
          className={styles.analyzeButton}
          style={{
            cursor: disableActions || isAnalyzing || mealText.trim().length === 0 ? 'not-allowed' : 'pointer',
            opacity: disableActions || isAnalyzing || mealText.trim().length === 0 ? 0.7 : 1,
          }}
        >
          {isAnalyzing ? t('food.addMeal.analyzing') : t('food.addMeal.analyze')}
        </button>
      </div>

      <div className={styles.grid}>
        <label className={styles.label}>
          {t('food.addMeal.calories')}
          <input
            type="number"
            value={analyzedCalories}
            onChange={(e) => onChangeCalories(Number(e.target.value))}
            min={0}
            step={1}
            className={styles.input}
          />
        </label>
        <label className={styles.label}>
          {t('food.addMeal.protein')}
          <input
            type="number"
            value={analyzedProtein}
            onChange={(e) => onChangeProtein(Number(e.target.value))}
            min={0}
            step={1}
            className={styles.input}
          />
        </label>
        <label className={styles.label}>
          {t('food.addMeal.carbs')}
          <input
            type="number"
            value={analyzedCarbs}
            onChange={(e) => onChangeCarbs(Number(e.target.value))}
            min={0}
            step={1}
            className={styles.input}
          />
        </label>
        <label className={styles.label}>
          {t('food.addMeal.fats')}
          <input
            type="number"
            value={analyzedFats}
            onChange={(e) => onChangeFats(Number(e.target.value))}
            min={0}
            step={1}
            className={styles.input}
          />
        </label>
      </div>
    </SlideUpPanel>
  );
};

export default AddMealPanel;
